import clone from 'ramda/src/clone'

/**
   * Deduplicates consecutive identical event groups in a conversation history.
   *
   * An event group consists of an event message followed by its bot response messages.
   * Two consecutive event groups are considered identical if they have:
   * - The same event ID (message.data.event.id)
   * - The same sequence of bot messages (same content in the same order)
   *
   * When duplicate groups are found, only the last occurrence is kept with a
   * `duplicateCount` property indicating how many times it was repeated.
   *
   * @param {Array<Object>} messages - Array of message objects from the conversation history API
   * @returns {Array<Object>} Deduplicated array of messages with duplicateCount added to repeated events
   *
   * @example
   * // Input: Event A with messages M1, M2, then same Event A with same messages M1, M2
   * const messages = [
   *   { type: 'event', data: { event: { id: '123' } } },
   *   { type: 'message', data: { content: { payload: 'M1' }, direction: 'SENT', metadata: { amio_bot: true } } },
   *   { type: 'message', data: { content: { payload: 'M2' }, direction: 'SENT', metadata: { amio_bot: true } } },
   *   { type: 'event', data: { event: { id: '123' } } },
   *   { type: 'message', data: { content: { payload: 'M1' }, direction: 'SENT', metadata: { amio_bot: true } } },
   *   { type: 'message', data: { content: { payload: 'M2' }, direction: 'SENT', metadata: { amio_bot: true } } }
   * ]
   *
   * const result = EventDeduplicator.deduplicate(messages)
   * // Output: Event A (with duplicateCount: 2) followed by M1, M2
   * // result[0].duplicateCount === 2
   *
   * @example
   * // Input: Event A with M1, user message, Event A with M1 (not consecutive due to user message)
   * const messages = [
   *   { type: 'event', data: { event: { id: '123' } } },
   *   { type: 'message', data: { content: { payload: 'M1' }, direction: 'SENT', metadata: { amio_bot: true } } },
   *   { type: 'message', data: { content: { payload: 'User question' }, direction: 'RECEIVED' } },
   *   { type: 'event', data: { event: { id: '123' } } },
   *   { type: 'message', data: { content: { payload: 'M1' }, direction: 'SENT', metadata: { amio_bot: true } } }
   * ]
   *
   * const result = EventDeduplicator.deduplicate(messages)
   * // Output: No deduplication, all messages preserved because user message breaks the event groups
   * // result.length === 5
   */
class EventDeduplicator {
  /**
   * Deduplicate consecutive identical event groups in a message array.
   */
  static deduplicate(messages) {
    const groups = this._groupEventMessages(messages)
    return this._deduplicateGroups(groups)
  }

  static _groupEventMessages(messages) {
    const groups = []
    let i = 0
    
    while (i < messages.length) {
      const message = messages[i]
      
      if (this._isEventMessage(message)) {
        const { group, nextIndex } = this._collectEventGroup(messages, i)
        groups.push(group)
        i = nextIndex
      } else {
        groups.push({ message })
        i++
      }
    }
    
    return groups
  }

  static _collectEventGroup(messages, startIndex) {
    const event = messages[startIndex]
    const botMessages = []
    
    for (let i = startIndex + 1; i < messages.length; i++) {
      const msg = messages[i]
      
      if (this._isEventMessage(msg) || !this._isBotMessage(msg)) {
        return {
          group: { event, messages: botMessages },
          nextIndex: i
        }
      }
      
      botMessages.push(msg)
    }
    
    return {
      group: { event, messages: botMessages },
      nextIndex: messages.length
    }
  }

  static _deduplicateGroups(groups) {
    const messages = []
    let i = 0
    
    while (i < groups.length) {
      const group = groups[i]
      
      // Check if it's an event group (has 'event' property)
      if (group.event) {
        const { deduplicatedGroup, nextIndex } = this._processEventGroup(groups, i)
        messages.push(...deduplicatedGroup)
        i = nextIndex
      } else {
        messages.push(group.message)
        i++
      }
    }
    
    return messages
  }

  static _processEventGroup(groups, startIndex) {
    const firstGroup = groups[startIndex]
    const eventId = firstGroup.event.data?.event?.id
    let lastMatchIndex = startIndex
    let count = 1
    
    // Find all consecutive matching groups
    for (let i = startIndex + 1; i < groups.length; i++) {
      const nextGroup = groups[i]
      
      if (this._areGroupsIdentical(firstGroup, nextGroup, eventId)) {
        lastMatchIndex = i
        count++
      } else {
        break
      }
    }
    
    // Build deduplicated result
    const lastGroup = groups[lastMatchIndex]
    const event = this._createEventWithCount(lastGroup.event, count)
    
    return {
      deduplicatedGroup: [event, ...lastGroup.messages],
      nextIndex: lastMatchIndex + 1
    }
  }

  static _areGroupsIdentical(group1, group2, eventId) {
    return group2.event &&
           group2.event.data?.event?.id === eventId &&
           this._areMessageSequencesEqual(group1.messages, group2.messages)
  }

  static _createEventWithCount(event, count) {
    if (count <= 1) return event
    
    const eventCopy = clone(event)
    eventCopy.duplicateCount = count

    return eventCopy
  }

  static _isEventMessage(message) {
    return message.type === 'event' || message.type === 'bot_wake_up'
  }

  static _isBotMessage(message) {
    return message.data?.direction === 'SENT' &&
           message.data?.metadata?.amio_bot === true
  }

  static _areMessageSequencesEqual(messages1, messages2) {
    if (messages1.length !== messages2.length) return false
    
    return messages1.every((msg, i) =>
      msg.data?.content?.payload === messages2[i].data?.content?.payload
    )
  }
}

export default EventDeduplicator

